import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Card,
  Select,
  Button,
  Table,
  message,
  Modal,
  Form,
  Input,
  Tag,
  Space,
  Spin,
  Breadcrumb,
  Empty,
  Popconfirm,
  Tabs,
  Tooltip,
} from 'antd';
import { databricksService } from '../services/databricksService';
import { accountService } from '../services/accountService';
import { useAuth } from '../context/AuthContext';
import IsEnabled from '../rolepermission';
import { useTheme } from '../context/ThemeContext';

const { Option } = Select;

const DatabricksMetricsOnboardingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const isViewer = !IsEnabled('Accounts');

  // Debug logging
  console.log('DatabricksMetricsOnboardingPage - User:', user);
  console.log('DatabricksMetricsOnboardingPage - Location:', location);
  console.log('DatabricksMetricsOnboardingPage - IsViewer:', isViewer);

  // Account selection
  const [accountOptions, setAccountOptions] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedAccountType, setSelectedAccountType] = useState('aws');
  const [activeTab, setActiveTab] = useState('aws');
  const [isAccountLocked, setIsAccountLocked] = useState(false);

  // Databricks specific
  const [databricksAccounts, setDatabricksAccounts] = useState([]);
  const [selectedDatabricksAccount, setSelectedDatabricksAccount] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [clusters, setClusters] = useState([]);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);
  const [loadingClusters, setLoadingClusters] = useState(false);
  const [loadingInstanceProfiles, setLoadingInstanceProfiles] = useState(false);
  const [instanceProfiles, setInstanceProfiles] = useState([]);
  const [enablingMetrics, setEnablingMetrics] = useState(false);

  // Modal state
  const [databricksModalVisible, setDatabricksModalVisible] = useState(false);
  const [databricksForm] = Form.useForm();
  const [viewEditModalVisible, setViewEditModalVisible] = useState(false);
  const [viewingMetric, setViewingMetric] = useState(null);
  const [isViewMode, setIsViewMode] = useState(false);

  // Onboarded metrics list
  const [onboardedMetrics, setOnboardedMetrics] = useState([]);
  const [loadingMetrics, setLoadingMetrics] = useState(false);

  // Get account ID from URL (similar to logs page)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let accountId = params.get('accountId');
    
    // Handle accountId format like logs: "databricks_<accountId>" or just "<accountId>"
    if (accountId && accountId.startsWith('databricks_')) {
      accountId = accountId.replace('databricks_', '');
      setIsAccountLocked(true);
      setActiveTab('databricks');
    }
    
    if (accountId) {
      // Will be set after accounts are fetched
    }
  }, [location]);

  // Fetch accounts
  const fetchAccounts = useCallback(async () => {
    console.log('fetchAccounts called with user:', user);
    if (!user?.client_id) {
      console.log('No user client_id, returning early');
      return;
    }
    try {
      // Fetch AWS accounts
      const awsData = await accountService.getAccounts(user.client_id);
      const awsOptions = Object.entries(awsData).map(([accountId, details]) => ({
        value: `aws_${accountId}`,
        label: `${details.aws_account_name} (${accountId})`,
        type: 'aws',
        accountId: accountId,
      }));

      // Fetch Databricks accounts
      let databricksOptions = [];
      try {
        console.log('Fetching Databricks accounts...');
        const databricksAccountsList = await databricksService.getAccounts();
        console.log('Databricks accounts response:', databricksAccountsList);
        const accountsWithDetails = databricksAccountsList.map((account) => {
          const accountId = account.account_id;
          const accountName = account.account_name || `Databricks Account ${accountId}`;
          return {
            value: `databricks_${accountId}`,
            label: accountName,  // Display account name instead of account ID
            type: 'databricks',
            accountId: accountId,
            accountName: accountName,
            clientId: account.client_id,
            accountUrl: account.account_url || 'https://accounts.cloud.databricks.com',
          };
        });
        databricksOptions = accountsWithDetails;
        setDatabricksAccounts(accountsWithDetails);
      } catch (error) {
        console.error("Error fetching Databricks accounts:", error);
      }

      const allOptions = [...awsOptions, ...databricksOptions];
      setAccountOptions(allOptions);
      
      // Check for accountId in URL query parameters
      const urlParams = new URLSearchParams(window.location.search);
      const accountIdParam = urlParams.get('accountId');
      console.log('URL accountId param:', accountIdParam);
      
      if (accountIdParam && allOptions.length > 0) {
        let accountId = accountIdParam;
        if (accountId.startsWith('databricks_')) {
          accountId = accountId.replace('databricks_', '');
        }
        console.log('Processed accountId:', accountId);
        
        // Find the account in options
        const accountFromUrl = allOptions.find(opt => 
          opt.accountId === accountId || opt.value === accountIdParam
        );
        console.log('Found account from URL:', accountFromUrl);
        console.log('All options:', allOptions);
        
        if (accountFromUrl) {
          console.log('Setting account from URL');
          setSelectedAccount(accountFromUrl.value);
          setSelectedAccountType(accountFromUrl.type);
          setActiveTab(accountFromUrl.type);
          if (accountFromUrl.type === 'databricks') {
            const dbAccount = databricksOptions.find(acc => acc.accountId === accountFromUrl.accountId);
            console.log('Setting Databricks account:', dbAccount);
            setSelectedDatabricksAccount(dbAccount);
            setIsAccountLocked(true);
          }
        } else if (!selectedAccount) {
          console.log('Account not found in URL, using first option');
          // If account not found in URL, use first option
          setSelectedAccount(allOptions[0].value);
          setSelectedAccountType(allOptions[0].type);
          setActiveTab(allOptions[0].type);
        }
      } else if (allOptions.length > 0 && !selectedAccount) {
        const firstDatabricksAccount = allOptions.find(opt => opt.type === 'databricks');
        if (firstDatabricksAccount) {
          setSelectedAccount(firstDatabricksAccount.value);
          setSelectedAccountType(firstDatabricksAccount.type);
          setActiveTab(firstDatabricksAccount.type);
          const dbAccount = databricksOptions.find(acc => acc.accountId === firstDatabricksAccount.accountId);
          setSelectedDatabricksAccount(dbAccount);
        } else {
          setSelectedAccount(allOptions[0].value);
          setSelectedAccountType(allOptions[0].type);
          setActiveTab(allOptions[0].type);
        }
      }
    } catch (err) {
      console.error("Error fetching accounts:", err);
      message.error("Failed to fetch accounts");
    }
  }, [user]);

  // Define loadOnboardedMetrics function to fetch from API
  const loadOnboardedMetrics = useCallback(async (account = null) => {
    console.log('loadOnboardedMetrics called with account:', account);
    setLoadingMetrics(true);
    try {
      // Fetch monitored metrics from API (with optional account filtering)
      const accountId = account?.accountId || null;
      console.log('Fetching metrics for account:', accountId);
      const apiMetrics = await databricksService.getMonitoredMetrics(accountId);
      console.log('API metrics response:', apiMetrics);
      
      // Transform API response to match expected format
      const transformedMetrics = apiMetrics.map((metric, index) => ({
        id: `${metric.account_id}-${metric.workspace_id}-${metric.cluster_id}`,
        accountId: metric.account_id,
        accountName: `Databricks Account ${metric.account_id}`,
        workspaceId: metric.workspace_id,
        workspaceName: metric.workspace_name || `Workspace ${metric.workspace_id}`,
        deploymentName: metric.deployment_name || 'N/A',
        clusterId: metric.cluster_id,
        clusterName: metric.cluster_name || `Cluster ${metric.cluster_id}`,
        status: metric.status === 'active' ? 'enabled' : metric.status || 'enabled',
        enabledAt: metric.enabled_at || new Date().toISOString(),
        ampWorkspace: metric.amp_workspace,
        ampRegion: metric.amp_region,
        ...metric // Include any additional fields from API
      }));
      
      console.log('Transformed metrics:', transformedMetrics);
      
      // Filter by account if specified
      const accountToUse = account || selectedDatabricksAccount;
      console.log('Account to filter by:', accountToUse);
      console.log('Available account IDs in metrics:', transformedMetrics.map(m => m.accountId));
      console.log('Is account locked:', isAccountLocked);
      console.log('Account parameter passed:', account);
      
      if (account !== null && accountToUse && accountToUse.accountId) {
        // Only filter when explicitly requested with an account
        const filtered = transformedMetrics.filter(m => m.accountId === accountToUse.accountId);
        console.log('Filtered metrics for account:', accountToUse.accountId, filtered);
        setOnboardedMetrics(filtered);
      } else {
        console.log('Setting all metrics (no filtering)');
        setOnboardedMetrics(transformedMetrics);
      }
    } catch (error) {
      console.error('Error loading monitored metrics from API:', error);
      // Fallback to localStorage if API fails
      try {
        const stored = localStorage.getItem('databricks_onboarded_metrics');
        if (stored) {
          const allMetrics = JSON.parse(stored);
          const accountToUse = account || selectedDatabricksAccount;
          if (accountToUse && (account || isAccountLocked || selectedDatabricksAccount)) {
            const filtered = allMetrics.filter(m => m.accountId === accountToUse.accountId);
            setOnboardedMetrics(filtered);
          } else {
            setOnboardedMetrics(allMetrics);
          }
        } else {
          setOnboardedMetrics([]);
        }
      } catch (localError) {
        console.error('Error loading from localStorage:', localError);
        setOnboardedMetrics([]);
      }
    } finally {
      setLoadingMetrics(false);
    }
  }, [selectedDatabricksAccount, isAccountLocked]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Simple function to load all metrics (same as "Show All Metrics" button)
  const loadAllMetrics = async () => {
    try {
      setLoadingMetrics(true);
      console.log('Loading all metrics without filtering...');
      const apiMetrics = await databricksService.getMonitoredMetrics();
      console.log('Raw API response:', apiMetrics);
      
      const transformedMetrics = apiMetrics.map((metric, index) => ({
        id: `${metric.account_id}-${metric.workspace_id}-${metric.cluster_id}`,
        accountId: metric.account_id,
        accountName: `Databricks Account ${metric.account_id}`,
        workspaceId: metric.workspace_id,
        workspaceName: metric.workspace_name || `Workspace ${metric.workspace_id}`,
        deploymentName: metric.deployment_name || 'N/A',
        clusterId: metric.cluster_id,
        clusterName: metric.cluster_name || `Cluster ${metric.cluster_id}`,
        status: metric.status === 'active' ? 'enabled' : metric.status || 'enabled',
        enabledAt: metric.enabled_at || new Date().toISOString(),
        ampWorkspace: metric.amp_workspace,
        ampRegion: metric.amp_region,
      }));
      
      console.log('Transformed metrics:', transformedMetrics);
      setOnboardedMetrics(transformedMetrics);
    } catch (error) {
      console.error('Error loading all metrics:', error);
      setOnboardedMetrics([]);
    } finally {
      setLoadingMetrics(false);
    }
  };

  // Simple effect to load metrics when account changes
  useEffect(() => {
    if (selectedDatabricksAccount && selectedDatabricksAccount.accountId) {
      console.log('Loading metrics for:', selectedDatabricksAccount.accountId);
      const loadMetrics = async () => {
        try {
          setLoadingMetrics(true);
          const apiMetrics = await databricksService.getMonitoredMetrics(selectedDatabricksAccount.accountId);
          console.log('Loaded metrics:', apiMetrics);
          
          const transformedMetrics = apiMetrics.map((metric) => ({
            id: `${metric.account_id}-${metric.workspace_id}-${metric.cluster_id}`,
            accountId: metric.account_id,
            accountName: `Databricks Account ${metric.account_id}`,
            workspaceId: metric.workspace_id,
            workspaceName: metric.workspace_name || `Workspace ${metric.workspace_id}`,
            deploymentName: metric.deployment_name || 'N/A',
            clusterId: metric.cluster_id,
            clusterName: metric.cluster_name || `Cluster ${metric.cluster_id}`,
            status: metric.status === 'active' ? 'enabled' : metric.status || 'enabled',
            enabledAt: metric.enabled_at || new Date().toISOString(),
          }));
          
          setOnboardedMetrics(transformedMetrics);
        } catch (error) {
          console.error('Error:', error);
          setOnboardedMetrics([]);
        } finally {
          setLoadingMetrics(false);
        }
      };
      loadMetrics();
    }
  }, [selectedDatabricksAccount]);

  const saveOnboardedMetrics = (metrics) => {
    try {
      localStorage.setItem('databricks_onboarded_metrics', JSON.stringify(metrics));
    } catch (error) {
      console.error('Error saving onboarded metrics:', error);
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    // Find first account of the selected type
    const accountOfType = accountOptions.find(opt => opt.type === key);
    if (accountOfType) {
      if (key === 'aws') {
        // For AWS, just set the account type but don't navigate immediately
        setSelectedAccountType(key);
        setSelectedAccount(accountOfType.value);
      } else if (key === 'databricks') {
        if (!isAccountLocked) {
          setSelectedAccount(accountOfType.value);
          setSelectedAccountType(key);
        }
        const dbAccount = databricksAccounts.find(acc => acc.accountId === accountOfType.accountId);
        setSelectedDatabricksAccount(dbAccount);
      }
    } else {
      setSelectedAccount(null);
      setSelectedAccountType(key);
      setOnboardedMetrics([]);
    }
  };

  const handleAccountChange = (value) => {
    console.log('Account changed to:', value);
    setSelectedAccount(value);
    const account = accountOptions.find(opt => opt.value === value);
    
    if (account) {
      setSelectedAccountType(account.type);
      if (account.type === 'databricks') {
        const dbAccount = databricksAccounts.find(acc => acc.accountId === account.accountId);
        console.log('Setting Databricks account:', dbAccount);
        setSelectedDatabricksAccount(dbAccount); // This will trigger the useEffect to load metrics
      } else if (account.type === 'aws') {
        // For AWS accounts, navigate to main metrics page
        navigate('/metrics');
      }
    }
  };

  const handleOpenDatabricksModal = async () => {
    if (!selectedDatabricksAccount) {
      message.error('Please select a Databricks account');
      return;
    }
    
    databricksForm.resetFields();
    setWorkspaces([]);
    setSelectedWorkspace(null);
    setClusters([]);
    setSelectedCluster(null);
    
    try {
      // Get client_id directly from backend API response
      const clientId = selectedDatabricksAccount.clientId;
      
      if (!clientId) {
        message.error('Account information not found. Please refresh the page.');
        return;
      }

      // Get stored credentials for PAT token only
      const storedCreds = getStoredCredentials(selectedDatabricksAccount.accountId);

      setLoadingWorkspaces(true);
      const workspacesData = await databricksService.getMetricsWorkspaces(selectedDatabricksAccount.accountId);
      setWorkspaces(workspacesData || []);
      
      // Form will use OAuth from account_id
      databricksForm.setFieldsValue({});
    } catch (error) {
      console.error('Failed to fetch workspaces:', error);
      message.error('Failed to fetch workspaces: ' + (error.message || 'Unknown error'));
    } finally {
      setLoadingWorkspaces(false);
    }

    setDatabricksModalVisible(true);
  };

  const handleWorkspaceChange = async (workspaceId) => {
    if (!workspaceId || !selectedDatabricksAccount) return;

    const workspace = workspaces.find(w => w.workspace_id === workspaceId);
    if (!workspace) return;

    setSelectedWorkspace(workspace);
    setClusters([]);
    setSelectedCluster(null);
    setLoadingClusters(true);
    setLoadingInstanceProfiles(true);

    try {
      const accountId = selectedDatabricksAccount?.accountId;
      const accountName = selectedDatabricksAccount?.accountName;
      
      // Validate: account_name or account_id must be provided (OAuth is required)
      if (!accountName && !accountId) {
        message.error('Account must be selected to use OAuth authentication.');
        setLoadingClusters(false);
        setLoadingInstanceProfiles(false);
        return;
      }

      // Extract deployment_name from workspace_url if missing
      let deploymentName = workspace.deployment_name;
      if (!deploymentName && workspace.workspace_url) {
        const urlParts = workspace.workspace_url.split('//');
        if (urlParts.length > 1) {
          deploymentName = urlParts[1].split('.')[0];
          console.log('[DatabricksMetricsOnboardingPage handleWorkspaceChange] Extracted deployment_name from URL:', deploymentName);
        }
      }
      
      if (!deploymentName || !deploymentName.trim()) {
        console.error('[DatabricksMetricsOnboardingPage handleWorkspaceChange] ERROR: deployment_name is missing or empty after extraction');
        message.error('Workspace deployment name is missing. Please select the workspace again.');
        setLoadingClusters(false);
        setLoadingInstanceProfiles(false);
        return;
      }

      // Fetch clusters and instance profiles in parallel
      const [clustersData, instanceProfilesData] = await Promise.all([
        databricksService.getMetricsClusters(
          workspace.workspace_id,
          deploymentName,
          accountId,  // Pass account_id as fallback
          accountName  // Prefer account_name for OAuth token generation
        ).catch(err => {
          console.error('[handleWorkspaceChange] ERROR fetching clusters:', err);
          throw err;
        }),
        databricksService.getInstanceProfiles(
          deploymentName,
          accountId,  // Pass account_id as fallback
          accountName  // Prefer account_name for OAuth token generation
        ).catch(err => {
          console.error('[handleWorkspaceChange] ERROR fetching instance profiles:', err);
          // Don't throw - allow clusters to load even if profiles fail
          message.warning('Failed to fetch instance profiles. Please ensure instance profiles are configured in your Databricks workspace.');
          return [];
        })
      ]);
      
      setClusters(clustersData || []);
      setInstanceProfiles(instanceProfilesData || []);
    } catch (error) {
      console.error('Failed to fetch clusters:', error);
      message.error('Failed to fetch clusters: ' + (error.message || 'Unknown error'));
    } finally {
      setLoadingClusters(false);
      setLoadingInstanceProfiles(false);
    }
  };

  const handleEnableMetrics = async (values) => {
    if (!selectedDatabricksAccount || !selectedWorkspace || !selectedCluster) {
      message.error('Please select account, workspace, and cluster');
      return;
    }

    setEnablingMetrics(true);
    try {
      const accountId = selectedDatabricksAccount?.accountId;
      const accountName = selectedDatabricksAccount?.accountName;
      const instanceProfileArn = values.instanceProfileArn || databricksForm.getFieldValue('instanceProfileArn');
      
      // Validate: account_name or account_id must be provided (OAuth is required)
      if (!accountName && !accountId) {
        message.error('Account must be selected to use OAuth authentication.');
        setEnablingMetrics(false);
        return;
      }

      // Extract deployment_name from workspace_url if missing
      let deploymentName = selectedWorkspace.deployment_name;
      if (!deploymentName && selectedWorkspace.workspace_url) {
        const urlParts = selectedWorkspace.workspace_url.split('//');
        if (urlParts.length > 1) {
          deploymentName = urlParts[1].split('.')[0];
          console.log('[handleEnableMetrics] Extracted deployment_name from URL:', deploymentName);
        }
      }
      
      if (!deploymentName || !deploymentName.trim()) {
        console.error('[handleEnableMetrics] ERROR: deployment_name is missing or empty');
        message.error('Workspace deployment name is missing. Please select the workspace again.');
        setEnablingMetrics(false);
        return;
      }

      // Validate instance profile ARN
      if (!instanceProfileArn || !instanceProfileArn.trim()) {
        message.error("Instance Profile ARN is required. Please select an instance profile.");
        setEnablingMetrics(false);
        return;
      }

      // Enable metrics
      const result = await databricksService.toggleMetrics(
        selectedWorkspace.workspace_id,
        deploymentName,
        selectedCluster.cluster_id,
        true,
        accountId,  // Pass account_id as fallback
        accountName,  // Prefer account_name for OAuth token generation
        null,  // No PAT token - backend will use OAuth from account_name/account_id
        instanceProfileArn
      );

      // Add to onboarded metrics list
      const newMetric = {
        id: `${selectedDatabricksAccount.accountId}-${selectedWorkspace.workspace_id}-${selectedCluster.cluster_id}`,
        accountId: selectedDatabricksAccount.accountId,
        accountName: selectedDatabricksAccount.accountName,
        workspaceId: selectedWorkspace.workspace_id,
        workspaceName: selectedWorkspace.workspace_name,
        deploymentName: selectedWorkspace.deployment_name,
        clusterId: selectedCluster.cluster_id,
        clusterName: selectedCluster.cluster_name,
        status: 'enabled',
        enabledAt: new Date().toISOString(),
      };

      // Load existing metrics
      const stored = localStorage.getItem('databricks_onboarded_metrics');
      const allMetrics = stored ? JSON.parse(stored) : [];
      const updatedMetrics = [...allMetrics.filter(m => m.id !== newMetric.id), newMetric];
      saveOnboardedMetrics(updatedMetrics);
      setOnboardedMetrics(updatedMetrics.filter(m => 
        !isAccountLocked || m.accountId === selectedDatabricksAccount.accountId
      ));

      message.success(`Metrics enabled successfully for cluster "${selectedCluster.cluster_name}"`);
      setDatabricksModalVisible(false);
      databricksForm.resetFields();
      setSelectedWorkspace(null);
      setSelectedCluster(null);
    } catch (error) {
      console.error('Failed to enable metrics:', error);
      message.error('Failed to enable metrics: ' + (error.message || 'Unknown error'));
    } finally {
      setEnablingMetrics(false);
    }
  };

  const handleDisableMetrics = async (metric) => {
    try {
      // Validate deployment_name
      let deploymentName = metric.deploymentName;
      if (!deploymentName || 
          deploymentName === 'undefined' || 
          deploymentName === 'null' || 
          deploymentName === null || 
          (typeof deploymentName === 'string' && deploymentName.trim() === '')) {
        console.log('[Disable Metrics] deployment_name missing, fetching from workspace...');
        try {
          const workspaces = await databricksService.getMetricsWorkspaces(metric.accountId);
          const workspace = workspaces.find(w => w.workspace_id === metric.workspaceId);
          if (workspace && workspace.deployment_name) {
            deploymentName = workspace.deployment_name;
            console.log('[Disable Metrics] Found deployment_name from workspace:', deploymentName);
          } else {
            throw new Error(`deployment_name not found for workspace ${metric.workspaceId}`);
          }
        } catch (error) {
          console.error('[Disable Metrics] Error fetching deployment_name:', error);
          message.error('deployment_name is required but not available. Please refresh the page and try again.');
          return;
        }
      }

      // OAuth token will be generated from account_name or account_id
      const accountName = metric.accountName;  // Use account_name if available
      await databricksService.toggleMetrics(
        metric.workspaceId,
        deploymentName,
        metric.clusterId,
        false,
        metric.accountId,  // Pass account_id as fallback
        accountName,  // Prefer account_name for OAuth token generation
        null,  // No PAT token - backend will use OAuth from account_name/account_id
        null   // instance_profile_arn not needed when disabling
      );

      // Remove from onboarded metrics list
      const stored = localStorage.getItem('databricks_onboarded_metrics');
      const allMetrics = stored ? JSON.parse(stored) : [];
      const updatedMetrics = allMetrics.filter(m => m.id !== metric.id);
      saveOnboardedMetrics(updatedMetrics);
      setOnboardedMetrics(updatedMetrics.filter(m => 
        !isAccountLocked || m.accountId === selectedDatabricksAccount?.accountId
      ));

      message.success('Metrics disabled successfully');
    } catch (error) {
      console.error('Failed to disable metrics:', error);
      message.error('Failed to disable metrics: ' + (error.message || 'Unknown error'));
    }
  };

  const getStoredCredentials = (accountId) => {
    try {
      const stored = localStorage.getItem(`databricks_credentials_${accountId}`);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return null;
    }
  };

  const handleViewMetric = (record) => {
    setViewingMetric(record);
    setIsViewMode(true);
    setViewEditModalVisible(true);
  };

  const handleEditMetric = async (record) => {
    setViewingMetric(record);
    setIsViewMode(false);
    
    // Find the account
    const dbAccount = databricksAccounts.find(acc => acc.accountId === record.accountId);
    if (!dbAccount) {
      message.error('Databricks account not found');
      return;
    }

    setSelectedDatabricksAccount(dbAccount);
    
    try {
      const storedCreds = getStoredCredentials(record.accountId);
      if (!storedCreds || !storedCreds.client_id || !storedCreds.client_secret) {
        message.error('Account credentials not found. Please re-onboard the account.');
        return;
      }

      // Fetch workspaces
      setLoadingWorkspaces(true);
      const workspacesData = await databricksService.getMetricsWorkspaces(record.accountId);
      setWorkspaces(workspacesData || []);
      
      // Find and set the workspace
      const workspace = workspacesData.find(w => w.workspace_id === record.workspaceId);
      if (workspace) {
        setSelectedWorkspace(workspace);
        
        // Pre-fill the form with workspace
        databricksForm.setFieldsValue({
          workspace: workspace.workspace_id,
        });
        
        // Fetch clusters using OAuth from account_id
        const accountId = selectedDatabricksAccount?.accountId;
        if (accountId) {
          setLoadingClusters(true);
          try {
            const clustersData = await databricksService.getMetricsClusters(
              workspace.workspace_id,
              workspace.deployment_name,
              accountId
            );
            setClusters(clustersData || []);
            
            // Find and set the cluster
            const cluster = clustersData.find(c => c.cluster_id === record.clusterId);
            if (cluster) {
              setSelectedCluster(cluster);
              // Update form with cluster
              databricksForm.setFieldsValue({
                cluster: cluster.cluster_id,
              });
            }
          } catch (error) {
            console.error('Failed to fetch clusters:', error);
            message.error('Failed to fetch clusters: ' + (error.message || 'Unknown error'));
          } finally {
            setLoadingClusters(false);
          }
        } else {
          // If no PAT token, user will need to enter it and then select workspace again
          message.info('Please enter PAT token to fetch clusters');
        }
      }
    } catch (error) {
      console.error('Failed to fetch workspaces:', error);
      message.error('Failed to fetch workspaces: ' + (error.message || 'Unknown error'));
    } finally {
      setLoadingWorkspaces(false);
    }
    
    setViewEditModalVisible(true);
  };

  const columns = [
    {
      title: 'SI',
      key: 'si',
      width: 80,
      render: (_, record, index) => (
        <span style={{ fontWeight: '500', color: isDark ? '#adbac7' : '#666' }}>
          {String(index + 1).padStart(2, '0')}
        </span>
      ),
    },
    {
      title: 'Account ID',
      dataIndex: 'accountId',
      key: 'accountId',
      render: (text) => <span>{text}</span>,
    },
    {
      title: 'Workspace Name',
      dataIndex: 'workspaceName',
      key: 'workspaceName',
    },
    {
      title: 'Cluster Name',
      dataIndex: 'clusterName',
      key: 'clusterName',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={status === 'enabled' ? 'success' : 'error'}
        >
          {status === 'enabled' ? 'Enabled' : 'Disabled'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Tooltip title="View">
            <Button
              style={{ background: "none", border: '0px', width: '10px' }}
              type="default"
              onClick={() => handleViewMetric(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              style={{ background: "none", border: '0px', width: '10px', margin: "12px" }}
              onClick={() => handleEditMetric(record)}
              size="small"
              disabled={isViewer}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to disable metrics for this cluster?"
            onConfirm={() => handleDisableMetrics(record)}
            okText="Yes"
            cancelText="No"
            disabled={isViewer}
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                danger
                style={{
                  color: '#ff4d4f',
                  cursor: isViewer ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  padding: '4px',
                  borderRadius: '4px',
                  transition: 'background-color 0.2s',
                  opacity: isViewer ? 0.5 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!isViewer) {
                    e.target.style.backgroundColor = '#fff2f0';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
                onClick={(e) => {
                  if (!isViewer) {
                    e.stopPropagation();
                  }
                }}
              >
                Delete
              </Button>
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Add loading state for debugging
  if (!user) {
    return <div>Loading user data...</div>;
  }

  if (!user.client_id) {
    return <div>Error: User client_id not found. Please log in again.</div>;
  }

  console.log('Rendering component with:', {
    activeTab,
    selectedAccount,
    selectedAccountType,
    accountOptions: accountOptions.length,
    onboardedMetrics: onboardedMetrics.length
  });

  return (
    <div>
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        style={{ marginBottom: '20px' }}
        items={[
          {
            key: 'aws',
            label: (
              <span style={{ color: activeTab === 'aws' ? '#1890ff' : (isDark ? '#adbac7' : '#666') }}>
                AWS Metrics
              </span>
            ),
            children: (
              <div>
                <div
                  style={{
                    marginBottom: 16,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "10px",
                  }}
                >
                  <h2>AWS Metrics Summary</h2>
                  <Space wrap>
                     <Select
                       placeholder="Select AWS Account"
                       options={accountOptions.filter(opt => opt.type === 'aws')}
                       style={{ width: 350, minWidth: 350 }}
                       onChange={handleAccountChange}
                       value={selectedAccount}
                       showSearch
                       filterOption={(input, option) =>
                         (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                       }
                       dropdownStyle={{ minWidth: 350 }}
                       getPopupContainer={(trigger) => trigger.parentElement}
                       popupMatchSelectWidth={false}
                     />
                    <Tooltip title="Refresh Metrics">
                      <Button onClick={() => {
                          // Navigate to main metrics page for AWS
                          navigate('/metrics');
                        }}
                        loading={loadingMetrics}
                        disabled={!selectedAccount}
                      />
                    </Tooltip>
                    <Button
                      className="create-btn"type="primary"
                      onClick={() => {
                        // Navigate to main metrics page for AWS
                        navigate('/metrics');
                      }}
                      disabled={!selectedAccount || isViewer}
                    >
                      Manage AWS Metrics
                    </Button>
                  </Space>
                </div>
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px',
                  background: isDark ? '#1c2128' : '#fafafa',
                  borderRadius: '8px',
                  border: '1px dashed #d9d9d9'
                }}>
                  <h3>AWS Metrics Management</h3>
                  <p>AWS metrics are managed on the main metrics page.</p>
                  <Button 
                    type="primary" 
                    onClick={() => navigate('/metrics')}>
                    Go to AWS Metrics
                  </Button>
                </div>
              </div>
            ),
          },
          {
            key: 'databricks',
            label: (
              <span style={{ color: activeTab === 'databricks' ? '#1890ff' : (isDark ? '#adbac7' : '#666') }}>
                Databricks Metrics
              </span>
            ),
            children: (
              <div>
                <div
                  style={{
                    marginBottom: 16,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "10px",
                  }}
                >
                  <h2>Databricks Metrics Summary</h2>
                  <Space wrap>
                     <Select
                       placeholder="Select Databricks Account"
                       options={accountOptions.filter(opt => opt.type === 'databricks')}
                       style={{ width: 350, minWidth: 350 }}
                       onChange={handleAccountChange}
                       value={selectedAccount}
                       showSearch
                       filterOption={(input, option) =>
                         (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                       }
                       dropdownStyle={{ minWidth: 350 }}
                       getPopupContainer={(trigger) => trigger.parentElement}
                       popupMatchSelectWidth={false}
                     />
                    <Tooltip title="Refresh Metrics">
                      <Button onClick={() => {
                          if (selectedDatabricksAccount) {
                            // Reload metrics for current account
                            const loadMetrics = async () => {
                              try {
                                setLoadingMetrics(true);
                                const apiMetrics = await databricksService.getMonitoredMetrics(selectedDatabricksAccount.accountId);
                                const transformedMetrics = apiMetrics.map((metric) => ({
                                  id: `${metric.account_id}-${metric.workspace_id}-${metric.cluster_id}`,
                                  accountId: metric.account_id,
                                  accountName: `Databricks Account ${metric.account_id}`,
                                  workspaceId: metric.workspace_id,
                                  workspaceName: metric.workspace_name || `Workspace ${metric.workspace_id}`,
                                  deploymentName: metric.deployment_name || 'N/A',
                                  clusterId: metric.cluster_id,
                                  clusterName: metric.cluster_name || `Cluster ${metric.cluster_id}`,
                                  status: metric.status === 'active' ? 'enabled' : metric.status || 'enabled',
                                  enabledAt: metric.enabled_at || new Date().toISOString(),
                                }));
                                setOnboardedMetrics(transformedMetrics);
                              } catch (error) {
                                console.error('Error:', error);
                                setOnboardedMetrics([]);
                              } finally {
                                setLoadingMetrics(false);
                              }
                            };
                            loadMetrics();
                          }
                        }}
                        loading={loadingMetrics}
                        disabled={!selectedAccount}
                      />
                    </Tooltip>
                    <Button
                      className="create-btn"type="primary"
                      onClick={handleOpenDatabricksModal}
                      disabled={!selectedAccount || isViewer}
                    >
                      Enable Metrics
                    </Button>
                  </Space>
                </div>

                <Table
                  className="custom-ant-table rounded-rows-table"
                  columns={columns}
                  dataSource={onboardedMetrics}
                  loading={loadingMetrics}
                  rowKey="id"
                  pagination={false}
                  locale={{
                    emptyText: <Empty description="No Databricks metrics enabled yet" />,
                  }}
                />
              </div>
            ),
          },
        ]}
      />

      {/* Databricks Metrics Onboarding Modal */}
      <Modal
        title="Enable Databricks Metrics"
        open={databricksModalVisible}
        onCancel={() => {
          setDatabricksModalVisible(false);
          databricksForm.resetFields();
          setWorkspaces([]);
          setSelectedWorkspace(null);
          setClusters([]);
          setSelectedCluster(null);
          setInstanceProfiles([]);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={databricksForm}
          layout="vertical"
          onFinish={handleEnableMetrics}
        >
          <Form.Item
            label="Account ID"
          >
            <Input
              value={selectedDatabricksAccount?.accountId || ''}
              disabled
            />
          </Form.Item>

          <Form.Item
            name="workspace"
            label="Workspace"
            rules={[{ required: true, message: 'Please select a workspace' }]}
          >
            <Select
              placeholder="Select workspace"
              onChange={handleWorkspaceChange}
              loading={loadingWorkspaces}
            >
              {workspaces.map((ws) => (
                <Option key={ws.workspace_id} value={ws.workspace_id}>
                  {ws.workspace_name || `Workspace ${ws.workspace_id}`}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="cluster"
            label="Cluster"
            rules={[{ required: true, message: 'Please select a cluster' }]}
          >
            <Select
              placeholder="Select cluster"
              onChange={(clusterId) => {
                const cluster = clusters.find(c => c.cluster_id === clusterId);
                setSelectedCluster(cluster);
              }}
              loading={loadingClusters}
              disabled={!selectedWorkspace || loadingClusters}
            >
              {clusters.map((cluster) => (
                <Option key={cluster.cluster_id} value={cluster.cluster_id}>
                  {cluster.cluster_name || `Cluster ${cluster.cluster_id}`} ({cluster.state || 'UNKNOWN'})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="instanceProfileArn"
            label="Instance Profile ARN"
            rules={[{ required: true, message: 'Please select an instance profile' }]}
          >
            <Select
              placeholder="Select instance profile"
              loading={loadingInstanceProfiles}
              disabled={!selectedWorkspace || loadingInstanceProfiles}
              options={instanceProfiles.map((profile) => ({
                value: profile.arn,
                label: profile.name || profile.arn,
              }))}
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={enablingMetrics}
              disabled={!selectedWorkspace || !selectedCluster}
            >
              Enable Metrics
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* View Metrics Modal */}
      <Modal
        title="View Databricks Metrics"
        open={viewEditModalVisible && isViewMode}
        onCancel={() => {
          setViewEditModalVisible(false);
          setViewingMetric(null);
          setIsViewMode(false);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setViewEditModalVisible(false);
            setViewingMetric(null);
            setIsViewMode(false);
          }}>
            Close
          </Button>
        ]}
        width={600}
      >
        {viewingMetric && (
          <Form layout="vertical">
            <Form.Item label="Account ID">
              <Input value={viewingMetric.accountId} disabled />
            </Form.Item>
            <Form.Item label="Account Name">
              <Input value={viewingMetric.accountName} disabled />
            </Form.Item>
            <Form.Item label="Workspace Name">
              <Input value={viewingMetric.workspaceName} disabled />
            </Form.Item>
            <Form.Item label="Workspace ID">
              <Input value={viewingMetric.workspaceId} disabled />
            </Form.Item>
            <Form.Item label="Deployment Name">
              <Input value={viewingMetric.deploymentName} disabled />
            </Form.Item>
            <Form.Item label="Cluster Name">
              <Input value={viewingMetric.clusterName} disabled />
            </Form.Item>
            <Form.Item label="Cluster ID">
              <Input value={viewingMetric.clusterId} disabled />
            </Form.Item>
            <Form.Item label="Status">
              <Tag color={viewingMetric.status === 'enabled' ? 'success' : 'error'}
              >
                {viewingMetric.status === 'enabled' ? 'Enabled' : 'Disabled'}
              </Tag>
            </Form.Item>
            {viewingMetric.enabledAt && (
              <Form.Item label="Enabled At">
                <Input value={new Date(viewingMetric.enabledAt).toLocaleString()} disabled />
              </Form.Item>
            )}
          </Form>
        )}
      </Modal>

      {/* Edit Metrics Modal */}
      <Modal
        title="Edit Databricks Metrics"
        open={viewEditModalVisible && !isViewMode}
        onCancel={() => {
          setViewEditModalVisible(false);
          setViewingMetric(null);
          setIsViewMode(false);
          databricksForm.resetFields();
          setWorkspaces([]);
          setSelectedWorkspace(null);
          setClusters([]);
          setSelectedCluster(null);
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setViewEditModalVisible(false);
            setViewingMetric(null);
            setIsViewMode(false);
            databricksForm.resetFields();
            setWorkspaces([]);
            setSelectedWorkspace(null);
            setClusters([]);
            setSelectedCluster(null);
          }}>
            Cancel
          </Button>,
          <Button
            key="toggle"
            type="primary"
            onClick={async () => {
              if (viewingMetric && selectedWorkspace && selectedCluster) {
                try {
                  // OAuth token will be generated from account_id
                  // Toggle metrics status
                  if (viewingMetric.status === 'enabled') {
                    await handleDisableMetrics(viewingMetric);
                  } else {
                    // Call handleEnableMetrics - it uses selectedWorkspace and selectedCluster from state
                    await handleEnableMetrics({});
                  }
                  
                  setViewEditModalVisible(false);
                  setViewingMetric(null);
                  setIsViewMode(false);
                  databricksForm.resetFields();
                  setWorkspaces([]);
                  setSelectedWorkspace(null);
                  setClusters([]);
                  setSelectedCluster(null);
                } catch (error) {
                  console.error('Failed to update metrics:', error);
                }
              }
            }}
            loading={enablingMetrics}
            disabled={!selectedWorkspace || !selectedCluster}
          >
            {viewingMetric?.status === 'enabled' ? 'Disable Metrics' : 'Enable Metrics'}
          </Button>
        ]}
        width={600}
      >
        {viewingMetric && (
          <Form
            form={databricksForm}
            layout="vertical"
          >
            <Form.Item label="Account ID">
              <Input
                value={selectedDatabricksAccount?.accountId || viewingMetric.accountId}
                disabled
              />
            </Form.Item>

            {/* PAT Token removed - using OAuth from account_id */}

            <Form.Item
              name="workspace"
              label="Workspace"
              rules={[{ required: true, message: 'Please select a workspace' }]}
              initialValue={selectedWorkspace?.workspace_id}
            >
              <Select
                placeholder="Select workspace"
                onChange={handleWorkspaceChange}
                loading={loadingWorkspaces}
                value={selectedWorkspace?.workspace_id}
              >
                {workspaces.map((ws) => (
                  <Option key={ws.workspace_id} value={ws.workspace_id}>
                    {ws.workspace_name || `Workspace ${ws.workspace_id}`}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="cluster"
              label="Cluster"
              rules={[{ required: true, message: 'Please select a cluster' }]}
              initialValue={selectedCluster?.cluster_id}
            >
              <Select
                placeholder="Select cluster"
                onChange={(clusterId) => {
                  const cluster = clusters.find(c => c.cluster_id === clusterId);
                  setSelectedCluster(cluster);
                }}
                loading={loadingClusters}
                disabled={!selectedWorkspace || loadingClusters}
                value={selectedCluster?.cluster_id}
              >
                {clusters.map((cluster) => (
                  <Option key={cluster.cluster_id} value={cluster.cluster_id}>
                    {cluster.cluster_name || `Cluster ${cluster.cluster_id}`} ({cluster.state || 'UNKNOWN'})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default DatabricksMetricsOnboardingPage;
